import { useEffect, useState } from "react";
import { vaultApi } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Books, Plus, TrashSimple, X } from "@phosphor-icons/react";
import { toast } from "sonner";

const KINDS = [
    { key: "argument", label: "Argument" },
    { key: "template", label: "Template" },
    { key: "precedent", label: "Precedent" },
    { key: "clause", label: "Clause" },
    { key: "note", label: "Note" },
];

export default function Vault() {
    const [items, setItems] = useState([]);
    const [kind, setKind] = useState("all");
    const [open, setOpen] = useState(false);
    const [active, setActive] = useState(null);

    const load = async () => {
        const data = await vaultApi.list(kind === "all" ? undefined : kind);
        setItems(data);
    };

    useEffect(() => {
        load();
    }, [kind]);

    return (
        <div className="p-4 md:p-8 max-w-[1400px] mx-auto" data-testid="vault-page">
            <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
                <div className="rule-line pt-3">
                    <div className="text-xs uppercase tracking-[0.3em] text-muted-foreground mb-1">Chambers library</div>
                    <h1 className="font-serif text-4xl tracking-tight">Knowledge Vault</h1>
                </div>
                <div className="flex items-center gap-2">
                    <Select value={kind} onValueChange={setKind}>
                        <SelectTrigger className="w-[160px]" data-testid="vault-kind-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">All kinds</SelectItem>
                            {KINDS.map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button onClick={() => setActive(null)} data-testid="new-vault-btn"><Plus size={14} /> New</Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-2xl">
                            <DialogHeader><DialogTitle className="font-serif">{active ? "Edit item" : "Save to Vault"}</DialogTitle></DialogHeader>
                            <VaultForm
                                initial={active}
                                onDone={() => { setOpen(false); setActive(null); load(); }}
                            />
                        </DialogContent>
                    </Dialog>
                </div>
            </div>

            {!items.length ? (
                <div className="border border-dashed border-border rounded-md p-10 text-center">
                    <Books size={28} className="mx-auto text-muted-foreground mb-3" weight="duotone" />
                    <div className="font-serif text-xl mb-1">Your vault is empty</div>
                    <div className="text-sm text-muted-foreground">Save arguments, templates, precedents and clauses for reuse.</div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {items.map((i) => (
                        <div key={i.id} className="border border-border rounded-md bg-white p-5 hover:shadow-sm transition-all" data-testid={`vault-item-${i.id}`}>
                            <div className="flex items-start justify-between gap-2 mb-1">
                                <div>
                                    <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground mb-1">{i.kind}</div>
                                    <div className="font-serif text-xl leading-tight">{i.title}</div>
                                </div>
                                <div className="flex items-center gap-1 shrink-0">
                                    <button className="text-muted-foreground hover:text-destructive" onClick={async () => { if (confirm("Delete?")) { await vaultApi.remove(i.id); load(); } }}><TrashSimple size={15} /></button>
                                </div>
                            </div>
                            <p className="text-sm text-foreground/80 line-clamp-4 whitespace-pre-wrap">{i.body}</p>
                            <div className="flex flex-wrap gap-1 mt-3">
                                {i.court && <span className="chip chip-external">{i.court}</span>}
                                {i.issue && <span className="chip chip-doc">{i.issue}</span>}
                                {i.statute && <span className="chip chip-doc">{i.statute}</span>}
                                {(i.tags || []).map((t) => <span key={t} className="chip chip-ai">{t}</span>)}
                            </div>
                            <button className="text-xs text-primary mt-3" onClick={() => { setActive(i); setOpen(true); }}>Edit →</button>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function VaultForm({ initial, onDone }) {
    const [v, setV] = useState(
        initial || {
            title: "",
            body: "",
            kind: "argument",
            tags: [],
            court: "",
            issue: "",
            judge: "",
            matter_type: "",
            statute: "",
            language: "en",
        },
    );
    const [saving, setSaving] = useState(false);
    const [tag, setTag] = useState("");

    const save = async () => {
        if (!v.title.trim() || !v.body.trim()) {
            toast.error("Title and body required");
            return;
        }
        setSaving(true);
        try {
            if (initial) await vaultApi.update(initial.id, v);
            else await vaultApi.create(v);
            toast.success("Saved");
            onDone?.();
        } catch {
            toast.error("Save failed");
        }
        setSaving(false);
    };

    return (
        <div className="space-y-3" data-testid="vault-form">
            <L label="Title"><Input value={v.title} onChange={(e) => setV({ ...v, title: e.target.value })} data-testid="vault-title" /></L>
            <L label="Kind">
                <Select value={v.kind} onValueChange={(val) => setV({ ...v, kind: val })}>
                    <SelectTrigger data-testid="vault-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>{KINDS.map((k) => <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>)}</SelectContent>
                </Select>
            </L>
            <L label="Body"><Textarea rows={6} value={v.body} onChange={(e) => setV({ ...v, body: e.target.value })} data-testid="vault-body" /></L>
            <div className="grid grid-cols-2 gap-3">
                <L label="Court"><Input value={v.court || ""} onChange={(e) => setV({ ...v, court: e.target.value })} placeholder="e.g., Delhi HC" /></L>
                <L label="Issue"><Input value={v.issue || ""} onChange={(e) => setV({ ...v, issue: e.target.value })} placeholder="e.g., limitation" /></L>
                <L label="Statute"><Input value={v.statute || ""} onChange={(e) => setV({ ...v, statute: e.target.value })} placeholder="e.g., CrPC 438" /></L>
                <L label="Matter type"><Input value={v.matter_type || ""} onChange={(e) => setV({ ...v, matter_type: e.target.value })} placeholder="e.g., Bail" /></L>
            </div>
            <L label="Tags">
                <div className="flex flex-wrap items-center gap-2">
                    {(v.tags || []).map((t) => (
                        <span key={t} className="chip chip-doc">
                            {t}
                            <button onClick={() => setV({ ...v, tags: v.tags.filter((x) => x !== t) })}><X size={10} /></button>
                        </span>
                    ))}
                    <Input
                        className="w-32"
                        placeholder="add tag"
                        value={tag}
                        onChange={(e) => setTag(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter" && tag.trim()) {
                                setV({ ...v, tags: [...(v.tags || []), tag.trim()] });
                                setTag("");
                                e.preventDefault();
                            }
                        }}
                    />
                </div>
            </L>
            <div className="sticky bottom-0 -mx-6 px-6 py-3 bg-background border-t border-border flex justify-end mt-2"><Button onClick={save} disabled={saving} data-testid="vault-save">{saving ? "Saving…" : "Save"}</Button></div>
        </div>
    );
}

function L({ label, children }) {
    return <div className="space-y-1"><div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{label}</div>{children}</div>;
}
